/**
 * P0-DOB-ONBOARDING-01 — the ONE patient date-of-birth input.
 *
 * Used by all three entry points (standard sign-up, the post-login /
 * legacy completion gate, Clinical profile → Personal information) so a
 * patient types their date of birth exactly the same way everywhere.
 *
 * Two interchangeable input methods over ONE value:
 *   A. keyboard — the existing shared `DateField` mask (TR `gg.aa.yyyy`,
 *      EN `mm/dd/yyyy`, numeric keypad on mobile, emits canonical ISO).
 *   B. calendar — react-day-picker with month AND year dropdowns, so a
 *      1972 birthday is two clicks away instead of 600 arrow presses.
 *
 * The value handed to the caller (and therefore to the API) is always
 * canonical ISO `YYYY-MM-DD`. Validation here is a UX convenience only —
 * `age_policy_service.assert_adult` on the server stays authoritative.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarIcon } from 'lucide-react';
import { DateField } from '../DateField';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Button } from '../ui/button';

export const MINIMUM_AGE_YEARS = 18;

// Parses `YYYY-MM-DD` as a CALENDAR date — never via `new Date(string)`,
// which applies a timezone shift and silently moves the day.
export function parseIsoDateParts(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso || '').trim());
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
  // Rejects 2023-02-29 and friends; leap years resolve correctly.
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) {
    return null;
  }
  return { year: y, month: mo, day: d };
}

export function toIsoDate(date) {
  if (!date) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function ageOnDate(iso, referenceIso) {
  const dob = parseIsoDateParts(iso);
  if (!dob) return null;
  const ref = referenceIso ? parseIsoDateParts(referenceIso) : null;
  const today = ref || (() => {
    const n = new Date();
    return { year: n.getFullYear(), month: n.getMonth() + 1, day: n.getDate() };
  })();
  let age = today.year - dob.year;
  if (today.month < dob.month || (today.month === dob.month && today.day < dob.day)) age -= 1;
  return age;
}

/** Returns an i18n key when invalid, or null when the date is acceptable. */
export function validateDobIso(iso, referenceIso) {
  const value = String(iso || '').trim();
  if (!value) return 'patient.dob.required';
  const parts = parseIsoDateParts(value);
  if (!parts) return 'patient.dob.invalid';
  const age = ageOnDate(value, referenceIso);
  if (age === null) return 'patient.dob.invalid';
  if (age < 0) return 'patient.dob.future';
  if (age < MINIMUM_AGE_YEARS) return 'patient.dob.under18';
  if (age > 120) return 'patient.dob.invalid';
  return null;
}

export const DateOfBirthField = ({
  value,
  onChange,
  label,
  disabled = false,
  testIdPrefix = 'dob',
  required = true,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const today = useMemo(() => new Date(), []);
  const maxDate = useMemo(
    () => new Date(today.getFullYear() - MINIMUM_AGE_YEARS, today.getMonth(), today.getDate()),
    [today],
  );
  const minDate = useMemo(() => new Date(today.getFullYear() - 120, 0, 1), [today]);
  const selected = useMemo(() => {
    const parts = parseIsoDateParts(value);
    return parts ? new Date(parts.year, parts.month - 1, parts.day) : undefined;
  }, [value]);

  return (
    <div className="flex items-end gap-2" data-testid={`${testIdPrefix}-field`}>
      <DateField
        value={value || ''}
        onChange={(iso) => onChange(iso)}
        label={label === undefined ? t('patient.dob.label') : label}
        required={required}
        disabled={disabled}
        className="w-44"
        inputClassName="bg-white"
        data-testid={`${testIdPrefix}-input`}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            disabled={disabled}
            aria-label={t('patient.dob.openCalendar')}
            className="mb-5 shrink-0 bg-white"
            data-testid={`${testIdPrefix}-calendar-trigger`}
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start" data-testid={`${testIdPrefix}-calendar`}>
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected || maxDate}
            captionLayout="dropdown-buttons"
            fromYear={minDate.getFullYear()}
            toYear={maxDate.getFullYear()}
            disabled={{ after: maxDate, before: minDate }}
            classNames={{
              caption_dropdowns: 'flex items-center justify-center gap-2',
              dropdown: 'rounded-md border border-slate-200 bg-white px-1 py-0.5 text-xs',
              dropdown_month: 'relative',
              dropdown_year: 'relative',
              caption_label: 'sr-only',
              nav: 'hidden',
            }}
            onSelect={(d) => {
              if (!d) return;
              onChange(toIsoDate(d));
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default DateOfBirthField;
